<template>
  <div
    v-show="!ssoLoginUrlInProgress"
    class="auth-wrapper auth-v2"
  >
    <b-row
      v-if="!ssoLoginUrlInProgress"
      class="auth-inner m-0"
    >

      <!-- Brand logo-->
      <b-link class="brand-logo">
        <vuexy-logo />
        <h2 class="brand-text text-primary ml-1">
          IpsosCX
        </h2>
      </b-link>
      <!-- /Brand logo-->

      <!-- Left Text-->
      <b-col
        lg="8"
        class="d-none d-lg-flex align-items-center p-5"
      >
        <div class="w-100 d-lg-flex align-items-center justify-content-center px-5">
          <b-img
            fluid
            :src="`${baseUrl()}/settings/download/backgroundImage`"
            onerror="this.style.display='none';document.getElementById('backup').style.display='block';"
            alt="Login V2"
          />
          <b-img
            id="backup"
            style="display: none;"
            fluid
            :src="imgUrl"
          />
        </div>
      </b-col>
      <!-- /Left Text-->

      <!-- Login-->
      <b-col
        lg="4"
        class="d-flex align-items-center auth-bg px-2 p-lg-5"
      >
        <b-col
          sm="8"
          md="6"
          lg="12"
          class="px-xl-2 mx-auto"
        >

          <div
            class="text-center"
            style="margin-bottom:100px"
          >
            <b-img
              fluid
              :src="`${baseUrl()}/settings/download/logoImage`"
              onerror="this.style.display='none'"
              alt="Logo"
              class="img-responsive"
            />
          </div>

          <b-card-title
            class="mb-1 font-weight-bold"
            title-tag="h2"
          >
            {{ $t('Welcome to CxPlus!') }} 👋
          </b-card-title>
          <b-card-text class="mb-2">
            {{ $t('If you do not know your password, please ') }}
            <b-link :to="{ name: 'auth-forgot-password' }">
              <small>{{ $t('click here') }}</small>
            </b-link>

          </b-card-text>

          <!-- form -->
          <validation-observer
            ref="loginForm"
            #default="{ invalid }"
          >
            <b-form
              class="auth-login-form mt-2"
              @submit.prevent="login"
            >
              <!-- email -->
              <b-form-group
                :label="$t('Email')"
                label-for="login-email"
              ><small
                 v-if="$route.name === 'auth-login-ipsos' && !isIpsosValid"
                 class="text-danger"
               ><strong>ipsos</strong>
                 ya da <strong>tro.ipsos-kmg</strong> mail adresinizi kullanınız!</small>
                <validation-provider
                  #default="{ errors }"
                  name="Email"
                  vid="email"
                  rules="required|email"
                >
                  <b-form-input
                    id="login-email"
                    v-model="userEmail"
                    :state="errors.length > 0 ? false : null"
                    name="login-email"
                    placeholder="john@example.com"
                    tabindex="1"
                  />
                  <small class="text-danger">{{ $t(errors[0]) }}</small>
                </validation-provider>
              </b-form-group>

              <!-- forgot password -->
              <b-form-group>
                <div class="d-flex justify-content-between">
                  <label for="login-password">{{ $t('Password') }}</label>
                  <b-link :to="{ name: 'auth-forgot-password' }">
                    <small>{{ $t('Forgot Password?') }}</small>
                  </b-link>
                </div>
                <validation-provider
                  #default="{ errors }"
                  name="Password"
                  vid="password"
                  rules="required"
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid' : null"
                  >
                    <b-form-input
                      id="login-password"
                      v-model="password"
                      :state="errors.length > 0 ? false : null"
                      class="form-control-merge"
                      :type="passwordFieldType"
                      name="login-password"
                      placeholder="Password"
                      autocomplete="false"
                      tabindex="2"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        class="cursor-pointer"
                        :icon="passwordToggleIcon"
                        @click="togglePasswordVisibility"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ $t(errors[0]) }}</small>
                </validation-provider>
              </b-form-group>

              <!-- submit buttons -->
              <b-button
                type="submit"
                variant="primary"
                block
                :disabled="invalid || inProgress || !isIpsosValid"
              >
                {{ btnText }}
              </b-button>

              <b-form-group class="mt-2">
                <v-select
                  id="locale"
                  v-model="locale"
                  :options="optionLocales"
                  :clearable="false"
                  :reduce="item => item.code"
                  label="label"
                  @input="setLocale"
                />
              </b-form-group>

              <faq-link :link-mode="true" />

            </b-form>
          </validation-observer>

        </b-col>
      </b-col>
      <!-- /Login-->
    </b-row>

  </div>
</template>

<script>

/* eslint-disable global-require */
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import VuexyLogo from '@core/layouts/components/Logo.vue'
import {
  BRow, BCol, BLink, BFormGroup, BFormInput, BInputGroupAppend, BInputGroup, BCardText, BCardTitle, BImg, BForm, BButton, VBTooltip,
} from 'bootstrap-vue'
import useJwt from '@/auth/jwt/useJwt'
import { required, email } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import store from '@/store/index'
import { getHomeRouteForLoggedInUser } from '@/auth/utils'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { refreshMenu, periodList } from '@core/layouts/utils'
import vSelect from 'vue-select'
import dayjs from 'dayjs'
import { baseUrl } from '@/libs/axios'
import FaqLink from '@/layouts/components/FaqLink.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BRow,
    BCol,
    BLink,
    BFormGroup,
    BFormInput,
    BInputGroupAppend,
    BInputGroup,
    BCardText,
    BCardTitle,
    BImg,
    BForm,
    BButton,
    VuexyLogo,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    FaqLink,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      twoFactor: false,
      status: '',
      password: null,
      userEmail: null,
      // sideImg: require('@/assets/images/pages/bg.jpeg'),
      sideImg: require('@/assets/images/pages/login-v2.svg'),

      // validation rules
      required,
      baseUrl,
      email,
      locale: 'tr',
      optionLocales: [{ code: 'tr', label: 'Türkçe' }, { code: 'en', label: 'English' }],
      inProgress: false,
      btnText: this.$t('Sign in'),
      // bgImageInProgress: true,
      // logoImg: null,
      // logoImageInProgress: true,
      ssoLoginUrlInProgress: true,
      ssoLoginUrl: null,
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    imgUrl() {
      return this.sideImg
    },
    // logoImgUrl() {
    //   return this.logoImg
    // },
    isIpsosValid() {
      if (this.$route.name !== 'auth-login-ipsos') {
        return true
      }
      return this.$route.name === 'auth-login-ipsos' && (this.userEmail?.toLowerCase().indexOf('cagatay.bulanik@') > -1 || this.userEmail?.toLowerCase().indexOf('@ipsos.com') > -1 || this.userEmail?.toLowerCase().indexOf('@tro.ipsos-kmg.com') > -1)
    },
  },
  mounted() {
    if (localStorage.getItem('locale')) {
      this.locale = localStorage.getItem('locale')
    }
  },
  async created() {
    await this.checkTwoFactor()
    await this.getSsoLoginUrl()
  },
  methods: {
    setLocale() {
      localStorage.setItem('locale', this.locale)
      this.$i18n.locale = this.locale
    },
    async checkTwoFactor() {
      await useJwt.axiosIns.get('settings/TwoFactor')
        .then(response => {
          this.twoFactor = response.data
        })
        .catch(e => {
          console.log(e)
        })
    },
    async goto() {
      await refreshMenu()
      await periodList()
    },
    async loggedIn(data) {
      const { userData } = data.data
      useJwt.setToken(data.data.accessToken)
      useJwt.setRefreshToken(data.data.refreshToken)
      localStorage.setItem('userData', JSON.stringify(userData))
      this.$ability.update(userData.ability)

      await this.goto()
      const { menuLinks } = store.state.verticalMenu
      if (userData.role === 'superadmin' || userData.role === 'admin') {
        this.$router.push(getHomeRouteForLoggedInUser(userData.role))
      } else if (menuLinks) {
        // check maintenance for regular users
        useJwt.axiosIns.get('settings/MaintenanceMode')
          .then(response => {
            this.maintenanceMode = response.data
            if (this.maintenanceMode) {
              this.$router.push({ name: 'maintenance' })
            } else {
              const firstPage = menuLinks[0]

              this.$router.push({ name: 'dashboard', params: { id: firstPage.id } })
                .then(() => {
                  this.$toast({
                    component: ToastificationContent,
                    position: 'top-right',
                    props: {
                      title: `Welcome ${userData.fullName}`,
                      icon: 'CoffeeIcon',
                      variant: 'success',
                      text: `You have successfully logged in as ${userData.role}. Now you can start to explore!`,
                    },
                  })
                })
            }
          })
          .catch(e => {
            console.log(e)
          })
      }
    },
    showKeyInput() {
      this.$swal({
        title: this.$t('Two Factor Authentication'),
        text: this.$t('Please enter the code sent to your email.'),
        input: 'text',
        showCancelButton: true,
        confirmButtonText: this.$t('Ok'),
        cancelButtonText: this.$t('Cancel'),
        customClass: {
          input: 'form-control m-2',
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          useJwt.login({
            email: this.userEmail,
            password: this.password,
            twoFactorAuthKey: result.value,
          }).then(res => {
            if (res.data.userData) {
              this.continueLogin(res)
            } else {
              this.$swal({
                icon: 'error',
                title: this.$t('Failed!'),
                text: this.$t('Invalid code, please try again'),
                confirmButtonText: this.$t('Ok'),
                customClass: {
                  confirmButton: 'btn btn-danger',
                },
              }).then(() => {
                this.showKeyInput()
              })
            }
          })
            .catch(error => {
              console.log(error)
              this.$refs.loginForm.setErrors(error)
            })
        }
      })
    },
    async login() {
      this.$refs.loginForm.validate().then(success => {
        if (success) {
          this.inProgress = true
          this.btnText = this.$t('Please wait...')

          useJwt.login({
            email: this.userEmail,
            password: this.password,
          })
            .then(response => {
              this.inProgress = false
              this.btnText = this.$t('Sign in')

              if (!response.data.userData) {
                if (response.data.twoFactor) {
                  this.showKeyInput()
                } else {
                  let htmlMessage = ''
                  if (response.data.invalidLoginAttemp >= 5 && response.data.invalidLoginLock) {
                    htmlMessage += `${this.$t('Your account is locked until:')} ${dayjs(response.data.invalidLoginLock).format('HH:mm')}`
                  } else {
                    htmlMessage += `${this.$t('Please check your e-mail address/password and try again')}`
                    htmlMessage += `<br><small>${this.$t('Remaining Login Attemps:')} ${5 - response.data.invalidLoginAttemp}</small>`
                  }
                  this.$swal({
                    icon: 'error',
                    title: this.$t('Failed!'),
                    html: htmlMessage,
                    customClass: {
                      confirmButton: 'btn btn-danger',
                    },
                  })
                }
              } else {
                this.continueLogin(response)
              }
            })
            .catch(error => {
              console.log(error)
              this.$refs.loginForm.setErrors(error)
              this.inProgress = false
              this.btnText = this.$t('Sign in')
            })
        }
      })
    },
    continueLogin(response) {
      const legal = localStorage.getItem('legal')
      if (!legal) {
        this.$swal({
          title: this.$t('Legal Warning'),
          html: this.$t('legal'),
          width: '50%',
          icon: 'warning',
          showCancelButton: false,
          confirmButtonText: this.$t('Continue'),
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        }).then(result => {
          if (result.value) {
            localStorage.setItem('legal', true)
            this.loggedIn(response)
          }
        })
      } else {
        this.loggedIn(response)
      }
    },
    async getSsoLoginUrl() {
      await useJwt.axiosIns.get('settings/SsoLoginUrl')
        .then(response => {
          this.ssoLoginUrlInProgress = false
          if (response.data) {
            if (response.data) {
              this.ssoLoginUrl = response.data
              if (this.$route.name !== 'auth-login-ipsos') {
                window.location.href = this.ssoLoginUrl
              }
            }
          }
        })
        .catch(e => {
          console.log(e)
        })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
</style>
